import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

type Display = Database['public']['Tables']['displays']['Row'];

export interface DisplayWithOwner extends Display {
  user_profile?: {
    user_id: string;
    email: string;
    full_name: string;
    avatar_url?: string;
  };
}

// Valid display table columns based on schema analysis
const VALID_DISPLAY_COLUMNS = [
  'id', 'name', 'description', 'location', 'city', 'state', 'latitude', 'longitude',
  'display_type', 'holiday_type', 'holidays', 'year_started', 'image_url', 'video',
  'schedule', 'display_hours', 'opens_closes', 'fm_station', 'website', 'facebook',
  'parking_other', 'status', 'featured', 'tags', 'user_id', 'created_at', 'updated_at',
  // Cache of first 10 photo URLs
  'photos',
  'average_rating', 'rating_count', 'rating_distribution', 'views_count'
] as const;

// Helper function to safely filter display data to only include valid database columns
const filterDisplayData = (data: Partial<DisplayWithOwner>): Partial<Display> => {
  const filtered: Partial<Display> = {};
  const invalidFields: string[] = [];
  
  for (const key in data) {
    if (VALID_DISPLAY_COLUMNS.includes(key as any)) {
      const value = data[key as keyof typeof data];
      if (value !== undefined) {
        (filtered as any)[key] = value;
      }
    } else {
      invalidFields.push(key);
    }
  }
  
  // Explicitly remove any user_profile fields that might have leaked through
  if ('user_profile' in (filtered as any)) {
    delete (filtered as any).user_profile;
    invalidFields.push('user_profile (explicitly removed)');
  }
  
  // Remove the ID field from updates to avoid conflicts
  if ('id' in (filtered as any)) {
    delete (filtered as any).id;
    invalidFields.push('id (removed from update data)');
  }
  
  // Log debugging information
  if (invalidFields.length > 0) {
    console.log('Filtered out invalid display fields:', invalidFields);
  }
  console.log('Valid display data being sent to Supabase:', filtered);
  
  return filtered;
};

export function useAdminDisplays() {
  const [displays, setDisplays] = useState<DisplayWithOwner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch displays with owner information
  const fetchDisplays = async () => {
    try {
      setLoading(true);
      
      // Get the total count first
      const { count, error: countError } = await supabase
        .from('displays')
        .select('*', { count: 'exact', head: true });
      
      if (countError) throw countError;
      
      const totalCount = count || 0;
      const pageSize = 1000; // Max allowed by Supabase
      const allDisplays: any[] = [];
      
      // Fetch all displays in batches with owner information
      for (let offset = 0; offset < totalCount; offset += pageSize) {
        const { data, error } = await supabase
          .from('displays')
          .select(`
            *,
            user_profile:user_profiles(
              user_id,
              display_name,
              full_name,
              avatar_url
            )
          `)
          .order('created_at', { ascending: false })
          .range(offset, offset + pageSize - 1);
        
        if (error) throw error;
        if (data) allDisplays.push(...data);
      }
      
      setDisplays(allDisplays);
    } catch (err) {
      console.error('Error fetching displays:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch displays');
    } finally {
      setLoading(false);
    }
  };

  // Create new display
  const createDisplay = async (display: Partial<DisplayWithOwner>) => {
    try {
      // Use the safe filtering function to ensure only valid database columns are included
      const filteredDisplay = filterDisplayData(display);

      console.log('Filtered display for Supabase create:', filteredDisplay);

      const { data, error } = await supabase
        .from('displays')
        .insert([filteredDisplay])
        .select()
        .single();

      if (error) throw error;
      
      // Refresh displays list to get the new display with owner info
      await fetchDisplays();
      
      return data;
    } catch (err) {
      console.error('Error creating display:', err);
      throw err;
    }
  };

  // Update display
  const updateDisplay = async (id: number, updates: Partial<DisplayWithOwner>) => {
    try {
      // Validate that we have an ID
      if (!id) {
        throw new Error('Display ID is required for update');
      }

      // Debug: Check current authentication status
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('Auth error:', authError);
        throw new Error('Authentication failed');
      }
      if (!user) {
        throw new Error('User not authenticated');
      }
      console.log('Authenticated user:', user.email, 'ID:', user.id);

      // Use the safe filtering function to ensure only valid database columns are included
      const filteredUpdates = filterDisplayData(updates);
      
      // Explicitly set updated_at to ensure timestamp is updated
      filteredUpdates.updated_at = new Date().toISOString();

      console.log('Updating display:', id, 'with filtered data:', filteredUpdates);

      // Check if we have any valid updates after filtering
      if (Object.keys(filteredUpdates).length <= 1) { // Only updated_at
        throw new Error('No valid fields to update');
      }

      // Use regular client for admin operations - get the full response
      const { data, error } = await supabase
        .from('displays')
        .update(filteredUpdates)
        .eq('id', id)
        .select(); // Make sure to select to get the updated data

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      // Check if any rows were actually updated
      if (!data || data.length === 0) {
        throw new Error(`No display found with ID ${id} or update was blocked by permissions`);
      }

      console.log('Successfully updated display:', data[0].id);
      
      // Refresh displays list to get updated data with owner info
      await fetchDisplays();
      
      return data[0];
    } catch (err) {
      console.error('Error updating display:', err);
      throw err;
    }
  };

  // Upload image to displays storage bucket using admin API endpoint
  const uploadDisplayImage = async (file: File, displayId: string | number) => {
    console.log('[uploadDisplayImage] Starting upload via API endpoint:', {
      fileName: file.name,
      fileSize: file.size,
      displayId,
    });

    try {
      // Get auth token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        console.error('[uploadDisplayImage] Session error:', sessionError);
        throw new Error('Authentication required for file upload');
      }
      console.log('[uploadDisplayImage] Session obtained for user:', session.user.email);

      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('displayId', displayId.toString());

      console.log('[uploadDisplayImage] Calling API endpoint: /api/admin/upload-display-image');

      // Call admin API endpoint (uses service role key to bypass RLS)
      const response = await fetch('/api/admin/upload-display-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: formData,
      });

      console.log('[uploadDisplayImage] API response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
        console.error('[uploadDisplayImage] API error response:', errorData);
        throw new Error(errorData.error || 'Upload failed');
      }

      const data = await response.json();
      console.log('[uploadDisplayImage] Upload successful:', {
        path: data.path,
        url: data.url,
      });
      return {
        path: data.path,
        url: data.url,
      };
    } catch (err) {
      console.error('[uploadDisplayImage] Error uploading display image:', err);
      throw err;
    }
  };

  // Add photo rows to display_photos (triggers will refresh displays.photos)
  const addDisplayPhotos = async (displayId: number, urls: string[]) => {
    // Determine next sort_order
    const { data: last, error: lastErr } = await supabase
      .from('display_photos')
      .select('sort_order')
      .eq('display_id', displayId)
      .order('sort_order', { ascending: false })
      .limit(1);
    if (lastErr) throw lastErr;
    let start = (last && last[0]?.sort_order) ?? -1;
    const rows = urls.map((url, i) => ({ display_id: displayId, url, sort_order: start + i + 1 }));
    const { error } = await supabase.from('display_photos').insert(rows);
    if (error) throw error;
    // Refresh list cache
    await fetchDisplays();
  };

  const removeDisplayPhoto = async (displayId: number, url: string) => {
    const { error } = await supabase
      .from('display_photos')
      .delete()
      .eq('display_id', displayId)
      .eq('url', url);
    if (error) throw error;
    await fetchDisplays();
  };

  const reorderDisplayPhotos = async (displayId: number, urlsInOrder: string[]) => {
    for (let i = 0; i < urlsInOrder.length; i++) {
      const { error } = await supabase
        .from('display_photos')
        .update({ sort_order: i })
        .eq('display_id', displayId)
        .eq('url', urlsInOrder[i]);
      if (error) throw error;
    }
    await fetchDisplays();
  };

  // Find displays with incomplete information
  const findDisplaysMissingInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('displays')
        .select('*')
        .or('description.is.null,image_url.is.null,holiday_type.is.null,city.is.null')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error finding displays missing info:', err);
      return [];
    }
  };

  // Find potential duplicate displays
  const findDuplicateDisplays = async () => {
    try {
      const { data, error } = await supabase
        .from('displays')
        .select('id, name, location, city, latitude, longitude, created_at')
        .order('name');

      if (error) throw error;

      const duplicateGroups: { [key: string]: typeof data } = {};
      
      data?.forEach(display => {
        // Group by name or by location proximity
        const key = `${display.name?.toLowerCase().trim()}_${display.city?.toLowerCase().trim()}`;
        if (!duplicateGroups[key]) {
          duplicateGroups[key] = [];
        }
        duplicateGroups[key].push(display);
      });

      // Filter to only groups with multiple entries
      const duplicates = Object.entries(duplicateGroups)
        .filter(([_, displays]) => displays.length > 1)
        .map(([key, displays]) => ({ key, displays }));

      return duplicates;
    } catch (err) {
      console.error('Error finding duplicate displays:', err);
      return [];
    }
  };

  // Assign display to user
  const assignDisplayOwner = async (displayId: number, userId: string | null) => {
    try {
      const { data, error } = await supabase
        .from('displays')
        .update({ user_id: userId })
        .eq('id', displayId)
        .select();

      if (error) throw error;

      // Refresh displays list
      await fetchDisplays();
      
      return data[0];
    } catch (err) {
      console.error('Error assigning display owner:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchDisplays();
  }, []);

  return {
    displays,
    loading,
    error,
    fetchDisplays,
    createDisplay,
    updateDisplay,
    uploadDisplayImage,
    addDisplayPhotos,
    removeDisplayPhoto,
    reorderDisplayPhotos,
    findDisplaysMissingInfo,
    findDuplicateDisplays,
    assignDisplayOwner
  };
}